import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircleIcon, 
  ExclamationCircleIcon,
  ArrowPathIcon 
} from '@heroicons/react/24/outline';
import { buildApiUrl, API_CONFIG } from '../../utils/api';

const ConfirmAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      confirmAccount(token);
    } else {
      setStatus('error');
      setErrorMessage('Token de confirmación no válido');
    }
  }, [token]);

  const confirmAccount = async (token) => {
    try {
      const url = buildApiUrl(`${API_CONFIG.ENDPOINTS.CONFIRM}?token=${token}`);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token
        })
      });

      if (response.ok) {
        setStatus('success');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setStatus('error');
        setErrorMessage(errorData.message || 'Error al verificar la cuenta');
      }
    } catch (error) {
      console.error('Error confirming account:', error);
      setStatus('error');
      setErrorMessage('Ha ocurrido un error, reintenta nuevamente');
    }
  };

  const handleRetry = () => {
    if (token) {
      setStatus('loading');
      confirmAccount(token);
    }
  };

  const handleFinish = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 to-secondary-600 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="max-w-md w-full"
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
          {/* Loading State */}
          {status === 'loading' && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-20 h-20 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                    <ArrowPathIcon className="h-10 w-10 text-primary-600 dark:text-primary-400 animate-spin" />
                  </div>
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Verificando Cuenta
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Por favor espera mientras verificamos tu cuenta...
              </p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="flex justify-center mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="relative"
                >
                  <div className="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="h-10 w-10 text-green-600 dark:text-green-400" />
                  </div>
                  {/* Ripple effect */}
                  <motion.div
                    initial={{ scale: 1, opacity: 0.5 }}
                    animate={{ scale: 1.8, opacity: 0 }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="absolute inset-0 bg-green-400 rounded-full"
                  />
                </motion.div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                ¡Cuenta Verificada!
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                Tu cuenta ha sido verificada exitosamente. Ya puedes iniciar sesión.
              </p>
              <button
                onClick={handleFinish}
                className="w-full btn-primary py-3 text-lg font-semibold"
              >
                Finalizar
              </button>
            </motion.div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="flex justify-center mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                >
                  <div className="w-20 h-20 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                    <ExclamationCircleIcon className="h-10 w-10 text-red-600 dark:text-red-400" />
                  </div>
                </motion.div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Error de Verificación
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                {errorMessage || 'Ha ocurrido un error, reintenta nuevamente'}
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleRetry}
                  className="w-full btn-primary py-3 text-lg font-semibold"
                >
                  Reintentar
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full btn-secondary py-3 text-lg font-semibold"
                >
                  Ir al Login
                </button>
              </div>
            </motion.div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-white text-sm">
            ¿Necesitas ayuda? <a href="/support" className="font-semibold hover:underline">Contacta con soporte</a>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default ConfirmAccount;

























